import React, { Fragment } from 'react';
import { X } from 'lucide-react';

// --- Button ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  isLoading, 
  className = '', 
  ...props 
}) => {
  const baseStyle = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aero-500 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2";
  
  const variants = {
    primary: "bg-aero-600 text-white hover:bg-aero-700 shadow-sm",
    secondary: "bg-white text-aero-900 hover:bg-aero-50 border border-aero-200",
    danger: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    ghost: "hover:bg-aero-100 text-aero-700",
  };

  return (
    <button className={`${baseStyle} ${variants[variant]} ${className}`} disabled={isLoading || props.disabled} {...props}>
      {isLoading ? (
        <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
      ) : null}
      {children}
    </button>
  );
};

// --- Input ---
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input: React.FC<InputProps> = ({ label, error, className = '', ...props }) => {
  return (
    <div className="w-full space-y-1">
      {label && <label className="text-sm font-medium text-aero-900 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">{label}</label>}
      <input
        className={`flex h-10 w-full rounded-md border border-aero-300 bg-white px-3 py-2 text-sm placeholder:text-aero-400 focus:outline-none focus:ring-2 focus:ring-aero-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${error ? 'border-red-500' : ''} ${className}`}
        {...props}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

// --- Modal ---
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, maxWidth }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-aero-900/20 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className={`relative w-full ${maxWidth || 'max-w-lg'} max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl shadow-aero-900/10 animate-in zoom-in-95 duration-200`}>
        <div className="flex items-center justify-between border-b border-aero-100 p-4 flex-shrink-0">
          <h3 className="text-lg font-semibold text-aero-900">{title}</h3>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-aero-100 transition-colors">
            <X className="h-4 w-4 text-aero-500" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

// --- Card ---
export const Card: React.FC<{ children: React.ReactNode; className?: string; title?: string; action?: React.ReactNode }> = ({ children, className = '', title, action }) => {
  return (
    <div className={`rounded-xl border border-aero-200 bg-white text-slate-900 shadow-sm ${className}`}>
      {(title || action) && (
        <div className="flex flex-col space-y-1.5 p-6 pb-2 border-b border-aero-50">
           <div className="flex items-center justify-between">
            {title && <h3 className="font-semibold leading-none tracking-tight text-lg text-aero-800">{title}</h3>}
            {action && <div>{action}</div>}
           </div>
        </div>
      )}
      <div className="p-6 pt-4">{children}</div>
    </div>
  );
};

// --- Tabs ---
interface TabItem {
  id: string;
  label: string;
  content: React.ReactNode;
}

export const Tabs: React.FC<{ items: TabItem[] }> = ({ items }) => {
  const [activeTab, setActiveTab] = React.useState(items[0].id);

  return (
    <div className="w-full">
      <div className="flex border-b border-aero-200 overflow-x-auto">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 whitespace-nowrap ${
              activeTab === item.id
                ? 'border-aero-600 text-aero-800'
                : 'border-transparent text-aero-500 hover:text-aero-700 hover:border-aero-300'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="mt-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
        {items.find((item) => item.id === activeTab)?.content}
      </div>
    </div>
  );
};